import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import './TrackOrder.css';
import Button from '../../components/UI/Button/Button';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import AuthContext from '../../context/AuthContext';
import LanguageContext from '../../context/LanguageContext.js';
import { getFoodOrderByNumber, searchOrdersByContact, getUserOrderHistory } from '../../api/orderApi';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import { formatOrderMoney } from '../../utils/currencyFormat';

const TrackOrder = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { currencyCode, storeLocale } = usePublicSettings();
    const moneyLocaleOpts = { locale: storeLocale };
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState('orderNumber');

    const [orderNumber, setOrderNumber] = useState(searchParams.get('orderNumber') || '');
    const [orderDetails, setOrderDetails] = useState(null);

    const [searchForm, setSearchForm] = useState({
        email: '',
        phone: ''
    });
    const [searchResults, setSearchResults] = useState([]);

    const [userOrders, setUserOrders] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleTrackOrder = useCallback(async (orderNum = orderNumber) => {
        if (!orderNum.trim()) {
            setError(t('trackOrder.enterOrderNumber'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await getFoodOrderByNumber(orderNum);
            if (response.status === 'success') {
                setOrderDetails(response.data);
                setSearchParams({ orderNumber: orderNum });
            } else {
                setError(response.message || t('trackOrder.orderNotFound'));
                setOrderDetails(null);
            }
        } catch (err) {
            console.error('Error tracking order:', err);
            setError(err.response?.data?.message || t('trackOrder.orderNotFound'));
            setOrderDetails(null);
        } finally {
            setLoading(false);
        }
    }, [orderNumber, setSearchParams, t]);

    const handleSearchOrders = async (e) => {
        e.preventDefault();

        if (!searchForm.email && !searchForm.phone) {
            setError(t('trackOrder.provideEmailOrPhone'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await searchOrdersByContact(searchForm);
            if (response.status === 'success') {
                setSearchResults(response.data);
                if (response.data.length === 0) {
                    setError(t('trackOrder.noOrdersFound'));
                }
            } else {
                setError(response.message || t('trackOrder.searchFailed'));
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Error searching orders:', err);
            setError(err.response?.data?.message || t('trackOrder.searchFailed'));
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const loadUserOrderHistory = useCallback(async () => {
        if (!user?._id) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getUserOrderHistory();
            if (response.status === 'success') {
                setUserOrders(response.data);
            } else {
                setError(response.message || t('trackOrder.loadHistoryFailed'));
            }
        } catch (err) {
            console.error('Error loading order history:', err);
            setError(err.response?.data?.message || t('trackOrder.loadHistoryFailed'));
        } finally {
            setLoading(false);
        }
    }, [user?._id, t]);

    const handleOrderNumberSubmit = (e) => {
        e.preventDefault();
        handleTrackOrder();
    };

    const handleSearchInputChange = (e) => {
        const { name, value } = e.target;
        setSearchForm(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (searchParams.get('orderNumber')) {
            handleTrackOrder(searchParams.get('orderNumber'));
        }
    }, [searchParams, handleTrackOrder]);

    useEffect(() => {
        if (activeTab === 'userOrders' && user?._id && userOrders.length === 0) {
            loadUserOrderHistory();
        }
    }, [activeTab, user?._id, loadUserOrderHistory, userOrders.length]);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'status-pending';
            case 'confirmed': return 'status-confirmed';
            case 'preparing': return 'status-preparing';
            case 'ready': return 'status-ready';
            case 'delivered': return 'status-delivered';
            case 'cancelled': return 'status-cancelled';
            default: return 'status-pending';
        }
    };

    const getStatusStep = (status) => {
        const steps = ['pending', 'confirmed', 'preparing', 'ready', 'delivered'];
        return steps.indexOf(status?.toLowerCase()) + 1;
    };

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'awaiting-payment': return t('myOrders.statusAwaitingPayment');
            case 'pending': return t('myOrders.statusPending');
            case 'confirmed': return t('myOrders.statusConfirmed');
            case 'preparing': return t('myOrders.statusPreparing');
            case 'ready': return t('myOrders.statusReady');
            case 'delivered': return t('myOrders.statusDelivered');
            case 'completed': return t('myOrders.statusCompleted');
            case 'cancelled': return t('myOrders.statusCancelled');
            default: return status;
        }
    };

    const renderSnippetCard = (order, key) => (
        <button
            type="button"
            key={key}
            className="track-snippet-card"
            onClick={() => setOrderDetails(order)}
        >
            <div className="track-snippet-head">
                <span className="track-snippet-number">#{order.orderNumber}</span>
                <span className={`track-snippet-status ${getStatusColor(order.orderStatus)}`}>
                    {getStatusText(order.orderStatus)}
                </span>
            </div>
            <div className="track-snippet-meta">
                <span>{new Date(order.createdAt).toLocaleDateString(currentLanguage)}</span>
                <span className="track-snippet-total">{formatOrderMoney(order.totalAmount, order.currency, currencyCode, moneyLocaleOpts)}</span>
            </div>
            <div className="track-snippet-preview">
                {(order.items?.slice(0, 2) ?? []).map((item, idx, arr) => (
                    <span key={idx}>
                        {item.name?.[currentLanguage] || item.name?.en}
                        {idx < arr.length - 1 && ', '}
                    </span>
                ))}
                {(order.items?.length ?? 0) > 2 && ` +${order.items.length - 2} ${t('trackOrder.more')}`}
            </div>
        </button>
    );

    return (
        <main className="main">
            <Breadcrumb title={t('trackOrder.title')} />
            <section className="track-order-page">
                <div className="container track-order-body" dir={dir()}>
                    <div className="track-order-panel">
                        <div className="track-order-tabs" role="tablist" aria-label={t('trackOrder.title')}>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'orderNumber'}
                                className={`track-order-tab ${activeTab === 'orderNumber' ? 'track-order-tab--active' : ''}`}
                                onClick={() => setActiveTab('orderNumber')}
                            >
                                {t('trackOrder.trackByOrderNumber')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'search'}
                                className={`track-order-tab ${activeTab === 'search' ? 'track-order-tab--active' : ''}`}
                                onClick={() => setActiveTab('search')}
                            >
                                {t('trackOrder.searchByContact')}
                            </button>
                            {user && (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'userOrders'}
                                    className={`track-order-tab ${activeTab === 'userOrders' ? 'track-order-tab--active' : ''}`}
                                    onClick={() => setActiveTab('userOrders')}
                                >
                                    {t('trackOrder.myOrders')}
                                </button>
                            )}
                        </div>

                        <div className="track-order-tab-panel">
                            {activeTab === 'orderNumber' && (
                                <form className="track-order-form" onSubmit={handleOrderNumberSubmit}>
                                    <div className="track-form-group">
                                        <label htmlFor="orderNumber">{t('trackOrder.orderNumber')}</label>
                                        <input
                                            type="text"
                                            id="orderNumber"
                                            value={orderNumber}
                                            onChange={(e) => setOrderNumber(e.target.value)}
                                            placeholder={t('trackOrder.enterOrderNumber')}
                                            required
                                        />
                                    </div>
                                    <Button type="submit" className="track-order-submit" disabled={loading}>
                                        {loading ? t('trackOrder.tracking') : t('trackOrder.trackOrder')}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'search' && (
                                <form className="track-order-form" onSubmit={handleSearchOrders}>
                                    <div className="track-form-group">
                                        <label htmlFor="email">{t('trackOrder.email')}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={searchForm.email}
                                            onChange={handleSearchInputChange}
                                            placeholder={t('trackOrder.enterEmail')}
                                        />
                                    </div>
                                    <div className="track-form-group">
                                        <label htmlFor="phone">{t('trackOrder.phone')}</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={searchForm.phone}
                                            onChange={handleSearchInputChange}
                                            placeholder={t('trackOrder.enterPhone')}
                                        />
                                    </div>
                                    <Button type="submit" className="track-order-submit" disabled={loading}>
                                        {loading ? t('trackOrder.searching') : t('trackOrder.searchOrders')}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'userOrders' && user && (
                                <div className="track-user-orders">
                                    <h2 className="track-subsection-title">{t('trackOrder.recentOrders')}</h2>
                                    {loading ? (
                                        <div className="track-order-loading">
                                            <div className="track-order-spinner" aria-hidden />
                                            <p>{t('trackOrder.loading')}</p>
                                        </div>
                                    ) : userOrders.length === 0 ? (
                                        <div className="track-order-empty">
                                            <p>{t('trackOrder.noOrders')}</p>
                                            <Button to={`/${currentLanguage}/menu`}>
                                                {t('trackOrder.startOrdering')}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="track-snippet-list">
                                            {userOrders.map((order) => renderSnippetCard(order, order._id))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="track-order-error" role="alert">
                                <p>{error}</p>
                            </div>
                        )}

                        {activeTab === 'search' && searchResults.length > 0 && (
                            <div className="track-search-results">
                                <h2 className="track-subsection-title">{t('trackOrder.searchResults')}</h2>
                                <div className="track-snippet-list">
                                    {searchResults.map((order) => renderSnippetCard(order, order._id))}
                                </div>
                            </div>
                        )}

                        {orderDetails && (
                            <div className="track-detail">
                                <div className="track-detail-hero">
                                    <h2 className="track-detail-title">{t('trackOrder.orderDetails')}</h2>
                                    <div className="track-detail-row track-detail-row--highlight">
                                        <span className="track-detail-k">{t('trackOrder.orderNumber')}</span>
                                        <span className="track-detail-v">{orderDetails.orderNumber}</span>
                                    </div>
                                    <div className="track-detail-row">
                                        <span className="track-detail-k">{t('trackOrder.status')}</span>
                                        <span className={`track-detail-badge ${getStatusColor(orderDetails.orderStatus)}`}>
                                            {getStatusText(orderDetails.orderStatus)}
                                        </span>
                                    </div>
                                </div>

                                <div className="track-timeline-block">
                                    <h3 className="track-detail-h3">{t('trackOrder.orderProgress')}</h3>
                                    <div className="track-timeline">
                                        <div className={`track-timeline-step ${getStatusStep(orderDetails.orderStatus) >= 1 ? 'track-timeline-step--active' : ''}`}>
                                            <div className="track-timeline-circle">1</div>
                                            <div className="track-timeline-body">
                                                <h4>{t('trackOrder.pending')}</h4>
                                                <p>{t('trackOrder.pendingDesc')}</p>
                                            </div>
                                        </div>
                                        <div className={`track-timeline-step ${getStatusStep(orderDetails.orderStatus) >= 2 ? 'track-timeline-step--active' : ''}`}>
                                            <div className="track-timeline-circle">2</div>
                                            <div className="track-timeline-body">
                                                <h4>{t('trackOrder.confirmed')}</h4>
                                                <p>{t('trackOrder.confirmedDesc')}</p>
                                            </div>
                                        </div>
                                        <div className={`track-timeline-step ${getStatusStep(orderDetails.orderStatus) >= 3 ? 'track-timeline-step--active' : ''}`}>
                                            <div className="track-timeline-circle">3</div>
                                            <div className="track-timeline-body">
                                                <h4>{t('trackOrder.preparing')}</h4>
                                                <p>{t('trackOrder.preparingDesc')}</p>
                                            </div>
                                        </div>
                                        <div className={`track-timeline-step ${getStatusStep(orderDetails.orderStatus) >= 4 ? 'track-timeline-step--active' : ''}`}>
                                            <div className="track-timeline-circle">4</div>
                                            <div className="track-timeline-body">
                                                <h4>{t('trackOrder.ready')}</h4>
                                                <p>{t('trackOrder.readyDesc')}</p>
                                            </div>
                                        </div>
                                        {orderDetails.customerInfo?.orderType === 'delivery' && (
                                            <div className={`track-timeline-step ${getStatusStep(orderDetails.orderStatus) >= 5 ? 'track-timeline-step--active' : ''}`}>
                                                <div className="track-timeline-circle">5</div>
                                                <div className="track-timeline-body">
                                                    <h4>{t('trackOrder.delivered')}</h4>
                                                    <p>{t('trackOrder.deliveredDesc')}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="track-detail-section">
                                    <h3 className="track-detail-h3">{t('trackOrder.orderSummary')}</h3>
                                    <div className="track-summary-grid">
                                        <div className="track-summary-cell">
                                            <span className="track-summary-k">{t('trackOrder.orderType')}</span>
                                            <span className="track-summary-v">{orderDetails.customerInfo?.orderType}</span>
                                        </div>
                                        <div className="track-summary-cell">
                                            <span className="track-summary-k">{t('trackOrder.totalAmount')}</span>
                                            <span className="track-summary-v track-summary-v--accent">{formatOrderMoney(orderDetails.totalAmount, orderDetails.currency, currencyCode, moneyLocaleOpts)}</span>
                                        </div>
                                        {orderDetails.estimatedCompletionTime && (
                                            <div className="track-summary-cell">
                                                <span className="track-summary-k">{t('trackOrder.estimatedTime')}</span>
                                                <span className="track-summary-v">
                                                    {new Date(orderDetails.estimatedCompletionTime).toLocaleTimeString(currentLanguage)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="track-detail-section">
                                    <h3 className="track-detail-h3">{t('trackOrder.orderItems')}</h3>
                                    <div className="track-items-list">
                                        {orderDetails.items?.map((item, index) => (
                                            <div key={index} className="track-item-row">
                                                <div className="track-item-info">
                                                    {item.primaryImage && (
                                                        <img
                                                            src={item.primaryImage}
                                                            alt={item.name?.[currentLanguage] || item.name?.en}
                                                            className="track-item-img"
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="track-item-name">{item.name?.[currentLanguage] || item.name?.en}</div>
                                                        {item.variant && (
                                                            <div className="track-item-variant">
                                                                {item.variant.size?.[currentLanguage] && (
                                                                    <span>{t('trackOrder.size')}: {item.variant.size[currentLanguage]}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className="track-item-qty">×{item.quantity}</span>
                                                <span className="track-item-price">{formatOrderMoney(item.price, orderDetails.currency, currencyCode, moneyLocaleOpts)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {orderDetails.customerInfo?.orderType === 'delivery' && orderDetails.customerInfo?.deliveryAddress && (
                                    <div className="track-detail-section">
                                        <h3 className="track-detail-h3">{t('trackOrder.deliveryAddress')}</h3>
                                        <div className="track-address">
                                            {orderDetails.customerInfo.deliveryAddress.street}
                                            {orderDetails.customerInfo.deliveryAddress.apartment && `, ${orderDetails.customerInfo.deliveryAddress.apartment}`}
                                            <br />
                                            {orderDetails.customerInfo.deliveryAddress.city}, {orderDetails.customerInfo.deliveryAddress.postalCode}
                                            <br />
                                            {orderDetails.customerInfo.deliveryAddress.country}
                                        </div>
                                    </div>
                                )}

                                {orderDetails.customerNotes && (
                                    <div className="track-detail-section">
                                        <h3 className="track-detail-h3">{t('trackOrder.specialInstructions')}</h3>
                                        <p className="track-notes">{orderDetails.customerNotes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="track-order-footer">
                            <Button to={`/${currentLanguage}/menu`}>
                                {t('trackOrder.continueShopping')}
                            </Button>
                            <Button to={`/${currentLanguage}/contact`} variant="outline">
                                {t('trackOrder.needHelp')}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default TrackOrder;
